import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
    TabBar,
    THSorted as THSortedBase,
    TH,
    TD,
    TR,
    LabeledCheckbox,
    colors,
    InfoIcon,
    IconButton,
    SkubotSpinner,
    Dropdown,
    DropdownItem,
    ChevronIcon,
    Text,
    SearchIcon,
} from "@commonsku/styles";
import { createDownload, getImageSrc } from "../../../utils";
import {
    CatalogCsvExportType,
    exportCatalogCsv,
    selectCatalogCsvExportLoading,
} from "../../../redux/catalog";
import {
    CatalogItem,
    CatalogItemsSortField,
    parseCatalogItemsSortParam,
} from "../../../types";
import type { BulkAction } from "../../BulkActionBar";
import { CatalogBulkDeletePopup } from "./CatalogBulkDeletePopup";
import { useBulkDeletePopup, type CatalogBulkDeleteItem } from "./useBulkDeletePopup";

export const TAB_UNPUBLISHED = "unpublished";
export const TAB_PUBLISHED = "published";

export type CatalogItemsTab = typeof TAB_UNPUBLISHED | typeof TAB_PUBLISHED;

export const optionValueFromSelect = (opt: unknown): string => {
    if (
        opt &&
        typeof opt === "object" &&
        "value" in opt &&
        typeof (opt as { value: unknown }).value === "string"
    ) {
        return (opt as { value: string }).value;
    }
    return "";
};

type CatalogSortDirection = "asc" | "desc";

export function getCatalogItemsSortOrder(
    sort: string | null,
    field: CatalogItemsSortField,
): CatalogSortDirection | null {
    const parsed = parseCatalogItemsSortParam(sort);
    if (!parsed || parsed.field !== field) {
        return null;
    }
    return parsed.direction;
}

export function useCatalogRowSelection(items: CatalogItem[]) {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        setSelectedIds(new Set());
    }, [items]);

    const allSelected = items.length > 0 && selectedIds.size === items.length;
    const someSelected = selectedIds.size > 0 && selectedIds.size < items.length;

    const toggleAll = () => {
        if (allSelected || someSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(items.map((item) => item.item_id)));
        }
    };

    const toggleItem = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    return {
        selectedIds,
        allSelected,
        someSelected,
        toggleAll,
        toggleItem,
    };
}

export const FiltersRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 16px;
    width: 100%;
    margin-bottom: 8px;
`;

export const FilterField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1 1 160px;
    min-width: 140px;
`;

export const FilterLabel = styled.span`
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
`;

export const FilterActions = styled.div`
    display: flex;
    align-items: flex-end;
    flex: 0 0 auto;
    margin-left: auto;
`;

const ProductActionsRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    width: 100%;
    margin-bottom: 16px;
`;

const ProductActionsHint = styled.span`
    font-size: 16px;
    line-height: 24px;
    width: fit-content;
    gap: 8px;
    display: flex;
    flex-direction: row;
`;

export const ItemsTabBar = styled(TabBar)`
    &&& {
        width: 100%;
        font-size: 18px;
        line-height: 24px;
        margin-bottom: 16px;
    }
`;

export const SearchInputShell = styled.div`
    position: relative;
    width: 100%;
`;

export const SearchTermInput = styled.input`
    &&& {
        margin: 0;
        width: 100%;
        box-sizing: border-box;
        padding-right: 36px;
    }
`;

export const SearchInputEndIcon = styled.span`
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    pointer-events: none;
`;

const CheckboxCell = styled.div`
    &&& {
        > * {
            margin: 0 !important;
        }
    }
`;

export const EmptyStateWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: auto;
    height: 55vh;
    max-width: 375px;
    align-self: center;
    gap: 16px;
`;

export const IconCircle = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background-color: ${colors.neutrals[20]}; // Light gray background
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const EmptyStateHeaderText = styled(Text)`
    line-height: 32px;
    font-size: 20px;
    font-weight: 600;
    font-family: "TT Norms Pro", sans-serif;
    cursor: default;
    color: ${colors.neutrals[100]};
    align-items: center;
`;

export const EmptyStateBodyText = styled(Text)<{ $blue?: boolean }>`
    vertical-align: middle;
    font-weight: 400;
    font-family: "skufont-regular";
    font-size: 16px;
    line-height: 24px;
    text-align: center;
    color: ${(props) =>
        props.$blue ? colors.primary1[65] : colors.neutrals[90]};
`;

export const EmptyItemStateComponent = () => (
    <EmptyStateWrapper>
        <IconCircle>
            <SearchIcon color={colors.neutrals[100]} />
        </IconCircle>
        <EmptyStateHeaderText>No products match your filters</EmptyStateHeaderText>
        <EmptyStateBodyText>Try adjusting your search or filters.</EmptyStateBodyText>
    </EmptyStateWrapper>
);

export const CatalogDropdownMenuItem = styled.p<{ $delete?: boolean; $disabled?: boolean }>`
    &&& {
        color: ${({ $delete, $disabled }) =>
            $delete ? colors.errors[50]
            : ($disabled ? colors.neutrals[50] : colors.neutrals[90])
        };
        font-weight: 400;
        margin-bottom: 0;
    }
`;

// @commonsku/styles THSorted types are incomplete — padded/clickable/onClick work at runtime
export const THSorted = THSortedBase as React.ComponentType<
    React.ComponentProps<typeof THSortedBase> & {
        padded?: boolean;
        clickable?: boolean;
        width?: string;
        onClick?: () => void;
    }
>;

export const TABLE_PARENT_PROPS = {
    style: { borderCollapse: "collapse" as const, borderSpacing: 0, width: "100%" },
};

const CHECKBOX_COLUMN_STYLE: React.CSSProperties = {
    width: 40,
    paddingLeft: 10,
};

const CHECKBOX_ROW_TD_STYLE: React.CSSProperties = {
    ...CHECKBOX_COLUMN_STYLE,
    verticalAlign: "middle",
};

export const PRIMARY_CELL_STYLE: React.CSSProperties = {
    color: "var(--color-primary1-main)",
};

export const ROW_ACTIONS_HEADER_STYLE: React.CSSProperties = {
    paddingLeft: 200,
    textAlign: "right",
};

const PRODUCT_THUMBNAIL_STYLE: React.CSSProperties = {
    width: 40,
    height: 40,
    objectFit: "cover",
    flexShrink: 0,
};

const PRODUCT_PLACEHOLDER_STYLE: React.CSSProperties = {
    width: 40,
    height: 40,
    flexShrink: 0,
    backgroundColor: "#e8edf0",
};

const PRODUCT_CELL_STYLE: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
};

interface CatalogSelectAllCheckboxProps {
    checked: boolean;
    indeterminate: boolean;
    onChange: () => void;
}

export const CatalogSelectAllCheckbox = ({
    checked,
    indeterminate,
    onChange,
}: CatalogSelectAllCheckboxProps) => (
    <TH style={CHECKBOX_COLUMN_STYLE}>
        <CheckboxCell>
            <LabeledCheckbox
                label=""
                checked={checked}
                indeterminate={indeterminate}
                onChange={onChange}
            />
        </CheckboxCell>
    </TH>
);

interface CatalogRowCheckboxProps {
    checked: boolean;
    onChange: () => void;
}

export const CatalogRowCheckbox = ({ checked, onChange }: CatalogRowCheckboxProps) => (
    <TD style={CHECKBOX_ROW_TD_STYLE}>
        <CheckboxCell>
            <LabeledCheckbox label="" checked={checked} onChange={onChange} />
        </CheckboxCell>
    </TD>
);

type CatalogProductLinkFields = Pick<
    CatalogItem,
    | "ext_product_id"
    | "parent_id"
    | "copied_from"
    | "supplier_code"
    | "sage_id"
    | "currency_id"
>;

export function buildCatalogProductUrl(item: CatalogProductLinkFields): string {
    const params = {
        id: item.ext_product_id || item.parent_id,
        source: item.copied_from || "",
        sku: item.supplier_code || "",
        sage_id: item.sage_id || "",
        currency_id: item.currency_id || "",
    };
    const query = Object.keys(params)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k as keyof typeof params])}`)
        .join("&");
    return `/product.php?${query}`;
}

interface CatalogProductCellProps {
    item: Pick<CatalogItem, "item_name" | "item_images"> & CatalogProductLinkFields;
}

export const CatalogProductCell = ({ item }: CatalogProductCellProps) => {
    const defaultImage = item.item_images?.[0]?.image;
    const href = buildCatalogProductUrl(item);

    return (
        <div style={PRODUCT_CELL_STYLE}>
            {defaultImage ? (
                <img src={getImageSrc(defaultImage)} alt={item.item_name} style={PRODUCT_THUMBNAIL_STYLE} />
            ) : (
                <div style={PRODUCT_PLACEHOLDER_STYLE} />
            )}
            <a href={href} target="_blank" rel="noreferrer" style={PRIMARY_CELL_STYLE}>
                {item.item_name}
            </a>
        </div>
    );
};

export function buildCatalogSupplierUrl(
    item: Pick<CatalogItem, "supplier_id" | "division_id">,
): string | null {
    if (!item.supplier_id) {
        return null;
    }
    const params = new URLSearchParams({ id: item.supplier_id });
    if (item.division_id) {
        params.set("division_id", item.division_id);
    }
    return `/vendor.php?${params.toString()}`;
}

interface CatalogSupplierCellProps {
    item: Pick<CatalogItem, "division_name" | "supplier_id" | "division_id">;
}

export const CatalogSupplierCell = ({ item }: CatalogSupplierCellProps) => {
    const label = item.division_name ?? "—";
    const href = buildCatalogSupplierUrl(item);

    if (!href || label === "—") {
        return <span style={PRIMARY_CELL_STYLE}>{label}</span>;
    }

    return (
        <a href={href} target="_blank" rel="noreferrer" style={PRIMARY_CELL_STYLE}>
            {label}
        </a>
    );
};

export const CatalogBulkLabel = ({ count }: { count: number }) => (
    <span style={{ color: "white", fontWeight: 600, fontSize: 18 }}>
        {count} product{count !== 1 ? "s" : ""} selected
    </span>
);

export const CatalogExportAction: BulkAction = ({ selected }) => (
    <IconButton onClick={() => console.log("Export:", selected)}>
        Export
    </IconButton>
);

export const CatalogDeleteAction: BulkAction<CatalogBulkDeleteItem> = ({ selected, setExecuting, disabled }) => {
    const {
        popupState,
        deleteInput,
        deleting,
        openDeletePopup,
        onDeleteInputChange,
        onCancel,
        onConfirm,
    } = useBulkDeletePopup();

    return (
        <>
            <IconButton
                preset="delete"
                onClick={() => openDeletePopup(selected, setExecuting)}
                disabled={disabled}
                style={{ outline: "none", boxShadow: "none" }}
            >
                Delete
            </IconButton>
            {popupState && (
                <CatalogBulkDeletePopup
                    count={popupState.items.length}
                    deleteInput={deleteInput}
                    onDeleteInputChange={onDeleteInputChange}
                    deleting={deleting}
                    onCancel={onCancel}
                    onConfirm={onConfirm}
                />
            )}
        </>
    );
};

interface CatalogProductActionsBarProps {
    action?: React.ReactNode;
    children: React.ReactNode;
}

export const CatalogProductActionsBar = ({ action, children }: CatalogProductActionsBarProps) => (
    <ProductActionsRow>
        {action}
        <ProductActionsHint>
            <InfoIcon size="medium" color={colors.black} />
            {children}
        </ProductActionsHint>
    </ProductActionsRow>
);

const CatalogExportDropdown = styled(Dropdown)<{ $hideMenu?: boolean }>`
    &&& {
        & div:nth-child(2) {
            inset-inline-start: auto;
            inset-inline-end: 0;
            ${({ $hideMenu }) => ($hideMenu ? "display: none;" : "")}
        }
    }
`;

interface CatalogExportActionsButtonDropdownProps {
    catalogId: string | null;
}

export const CatalogExportActionsButtonDropdown = ({
    catalogId,
}: CatalogExportActionsButtonDropdownProps) => {
    const dispatch = useDispatch();
    const exporting = useSelector(selectCatalogCsvExportLoading);
    const [menuOpen, setMenuOpen] = useState(false);
    const [dropdownKey, setDropdownKey] = useState(0);

    const handleExport = useCallback(async (type: CatalogCsvExportType) => {
        if (!catalogId || exporting) {
            return;
        }

        setMenuOpen(false);
        setDropdownKey((key) => key + 1);

        const result = await dispatch(
            exportCatalogCsv({ catalogId, type }) as any,
        );

        if (exportCatalogCsv.fulfilled.match(result)) {
            const { blob, type: exportType, catalogId: exportCatalogId } = result.payload;
            createDownload(
                URL.createObjectURL(blob),
                `client-catalog-${exportType}-${exportCatalogId}.csv`,
            );
        }
    }, [catalogId, dispatch, exporting]);

    const exportButton = (
        <IconButton
            type="button"
            variant="secondary"
            size="medium"
            iconPosition="right"
            disabled={!catalogId || exporting}
            iconProps={exporting ? {
                style: {
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 16,
                    height: 16,
                    verticalAlign: "middle",
                },
            } : undefined}
            Icon={
                exporting
                    ? <SkubotSpinner
                        size="button"
                        containerSize={16}
                        spinnerSize={16}
                        skubot={false}
                    />
                    : <ChevronIcon direction={menuOpen ? "up" : "down"} />
            }>
            Export
        </IconButton>
    );

    return (
        <CatalogExportDropdown
            key={dropdownKey}
            icon={exportButton}
            width="max-content"
            hideOnMouseLeave={!exporting}
            onToggleMenu={(open) => {
                if (catalogId && !exporting) {
                    setMenuOpen(open);
                }
            }}
            $hideMenu={exporting}
            style={!catalogId || exporting ? { pointerEvents: "none" } : undefined}
        >
            {!exporting && (
                <>
                    <DropdownItem onClick={() => handleExport("all")}>Export All</DropdownItem>
                    <DropdownItem onClick={() => handleExport("published")}>Export Published</DropdownItem>
                    <DropdownItem onClick={() => handleExport("unpublished")}>Export Unpublished</DropdownItem>
                </>
            )}
        </CatalogExportDropdown>
    );
};

export interface CatalogItemsListMeta {
    cursor: string | null;
    hasMore: boolean;
}

export const EMPTY_CATALOG_ITEMS_LIST_META: CatalogItemsListMeta = {
    cursor: null,
    hasMore: false,
};

export function parseCatalogItemsListMeta(meta: unknown): CatalogItemsListMeta {
    const m = meta as { cursor?: string; next_page?: string } | null | undefined;
    return {
        cursor: m?.cursor ?? null,
        hasMore: !!(m?.cursor || m?.next_page),
    };
}

export function mergeCatalogItemPages(
    existing: CatalogItem[],
    incoming: CatalogItem[],
    reset: boolean,
): CatalogItem[] {
    return reset ? incoming : [...existing, ...incoming];
}

interface CatalogItemsLoadMoreRowProps {
    colSpan: number;
    hasMore: boolean;
    loading: boolean;
    onLoadMore: () => void;
}

export const CatalogItemsLoadMoreRow = ({
    colSpan,
    hasMore,
    loading,
    onLoadMore,
}: CatalogItemsLoadMoreRowProps) => {
    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loading) {
                    onLoadMore();
                }
            },
            { rootMargin: "100px 0px" },
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loading, onLoadMore]);

    if (!hasMore && !loading) {
        return null;
    }

    return (
        <TR>
            <TD colSpan={colSpan} style={{ textAlign: "center", padding: "16px 0" }}>
                <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />
                {loading && <SkubotSpinner />}
            </TD>
        </TR>
    );
};

